//
// # Graphical Elements
//
// The stuff in "vector graphics" - paths, circles, text elements, etc.
//

import { Point, Bbox } from "../core";
import { exhaust } from "../errors";
import { Text } from "./text";
import { schema, Layered, ElementKind as Kind } from "../schema";

export type Rectangle = schema.Rectangle;
export const Rectangle = {
  new: (arg: Omit<Rectangle, "kind">): Rectangle => ({
    ...arg,
    kind: Kind.Rectangle,
  }),
  default: (): Rectangle => ({
    kind: Kind.Rectangle,
    bbox: Bbox.empty(),
    filled: false,
  }),
  fromPartial: (partial: Partial<Rectangle>): Rectangle => ({
    ...Rectangle.default(),
    ...partial,
  }),
  bbox: (rect: Rectangle): Bbox => {
    return rect.bbox;
  },
  bboxCenter: (rect: Rectangle): Point => {
    return Bbox.center(rect.bbox);
  },
  translate: (rect: Rectangle, delta: Point): Rectangle => ({
    ...rect,
    bbox: Bbox.translate(rect.bbox, delta),
  }),
};

export type Ellipse = schema.Ellipse;
export const Ellipse = {
  new: (arg: Omit<Ellipse, "kind">): Ellipse => ({
    ...arg,
    kind: Kind.Ellipse,
  }),
  default: (): Ellipse => ({
    kind: Kind.Ellipse,
    bbox: Bbox.empty(),
    filled: false,
  }),
  fromPartial: (partial: Partial<Ellipse>): Ellipse => ({
    ...Ellipse.default(),
    ...partial,
  }),
  bbox: (ellipse: Ellipse): Bbox => {
    return ellipse.bbox;
  },
  bboxCenter: (ellipse: Ellipse): Point => {
    return Bbox.center(ellipse.bbox);
  },
  translate: (ellipse: Ellipse, delta: Point): Ellipse => ({
    ...ellipse,
    bbox: Bbox.translate(ellipse.bbox, delta),
  }),
};

export type Arc = schema.Arc;
export const Arc = {
  new: (arg: Omit<Arc, "kind">): Arc => ({
    ...arg,
    kind: Kind.Arc,
  }),
  default: (): Arc => ({
    kind: Kind.Arc,
    ellipseBbox: Bbox.empty(),
    arcBbox: Bbox.empty(),
    startAngle: 0,
    stopAngle: 0,
  }),
  fromPartial: (partial: Partial<Arc>): Arc => ({
    ...Arc.default(),
    ...partial,
  }),
  bboxCenter: (arc: Arc): Point => {
    return Bbox.center(arc.ellipseBbox);
  },
  translate: (arc: Arc, delta: Point): Arc => ({
    ...arc,
    ellipseBbox: Bbox.translate(arc.ellipseBbox, delta),
    arcBbox: Bbox.translate(arc.arcBbox, delta),
  }),

  // Normalize an angle (radians) into the [0, 2π) range.
  normAngle: (a: number): number => {
    const twoPi = 2 * Math.PI;
    return ((a % twoPi) + twoPi) % twoPi;
  },
};

export type Path = schema.Path;
export const Path = {
  new: (arg: Omit<Path, "kind">): Path => ({
    ...arg,
    kind: Kind.Path,
  }),
  default: (): Path => ({
    kind: Kind.Path,
    points: [],
    style: PathStyle.default(),
  }),
  fromPartial: (partial: Partial<Path>): Path => ({
    ...Path.default(),
    ...partial,
  }),
  fromPoints: (points: Array<Point>): Path => Path.fromPartial({ points }),

  fromTuples: (...tuples: Array<[number, number]>): Path =>
    Path.fromPartial({
      points: tuples.map(([x, y]) => ({ x, y })),
    }),

  bboxCenter: (path: Path): Point => {
    return Bbox.center(Bbox.fromPoints(...path.points));
  },
  translate: (path: Path, delta: Point): Path => ({
    ...path,
    points: path.points.map((p) => Point.add(p, delta)),
  }),

  // The points to actually draw a path's centerline with: its first/last
  // point moved outward along the path's own tangent direction there by
  // `style.beginExt`/`endExt`, so a renderer can stroke this directly with
  // a flush ("butt") cap and get the right result regardless of extension
  // — the extension becomes real geometry instead of something a cap style
  // has to express (native canvas caps can only do 0 or half-linewidth,
  // not an arbitrary extension amount). A single-segment (2-point) path
  // extends correctly too: each endpoint's "toward" reference is just the
  // other endpoint.
  drawPoints: (path: Path): Array<Point> => {
    const { points, style } = path;
    if (points.length < 2) return points;
    const first = points[0];
    const second = points[1];
    const last = points[points.length - 1];
    const secondLast = points[points.length - 2];
    const extend = (from: Point, toward: Point, ext: number): Point => {
      if (ext === 0) return from;
      const len = Point.dist(from, toward);
      if (len === 0) return from;
      return Point.add(from, Point.scale(Point.sub(from, toward), ext / len));
    };
    return [
      extend(first, second, style.beginExt),
      ...points.slice(1, -1),
      extend(last, secondLast, style.endExt),
    ];
  },
};

export type PathStyle = schema.PathStyle;
// FIXME! resurrect these end styles
// & {
//   beginExt: number;
//   endExt: number;
// }
export const PathStyle = {
  default: () => ({
    // FIXME: are these good defaults? Time will tell.
    width: 2,
    beginExt: 0,
    endExt: 0,
    // beginStyle: PathSeg_EndStyle.Chamfer,
    // endStyle: PathSeg_EndStyle.Chamfer,
  }),
};

export enum PathSeg_EndStyle {
  Truncate = 0,
  Extend = 1,
  Variable = 2,
  Chamfer = 3,
  Custom = 4,
  UNRECOGNIZED = -1, // FIXME
}

export type Polygon = schema.Polygon;
export const Polygon = {
  new: (arg: Omit<Polygon, "kind">): Polygon => ({
    ...arg,
    kind: Kind.Polygon,
  }),
  default: (): Polygon => ({
    kind: Kind.Polygon,
    points: [],
    filled: false,
  }),
  fromTuples: (...tuples: Array<[number, number]>): Polygon => {
    return Polygon.new({
      points: tuples.map(([x, y]) => ({ x, y })),
      filled: false,
    });
  },
  fromPartial: (partial: Partial<Polygon>): Polygon => ({
    ...Polygon.default(),
    ...partial,
  }),
  bbox: (polygon: Polygon): Bbox => {
    return Bbox.fromPoints(...polygon.points);
  },
  bboxCenter: (polygon: Polygon): Point => {
    // FIXME: more elaborate center-calculations are certainly possible.
    // Here we take the center of the bounding box.
    return Bbox.center(Bbox.fromPoints(...polygon.points));
  },
  translate: (polygon: Polygon, delta: Point): Polygon => ({
    ...polygon,
    points: polygon.points.map((p) => Point.add(p, delta)),
  }),
};

// # Graphics Element Union
export type Layer = schema.Layer;
export type Element = schema.Element;
export const Element = {
  Kind,
  Rectangle,
  Polygon,
  Arc,
  Ellipse,
  Path,
  Text,
  // Bounding box of any `Element` kind, including `Text`.
  bbox: (elem: Element): Bbox => {
    switch (elem.kind) {
      case Kind.Rectangle:
        return Rectangle.bbox(elem);
      case Kind.Polygon:
        return Polygon.bbox(elem);
      case Kind.Ellipse:
        return Ellipse.bbox(elem);
      // TODO(aryap): This isn't the real bbox. The real bbox must consider the segment widths and the start/end extension of the Path.
      case Kind.Path:
        return Bbox.fromPoints(...elem.points);
      case Kind.Text:
        return Text.bbox(elem);
      case Kind.Arc:
        return elem.arcBbox;
      default:
        return exhaust(elem);
    }
  },
};

export type LayeredRectangle = schema.LayeredRectangle;
export type LayeredPath = schema.LayeredPath;
export type LayeredPolygon = schema.LayeredPolygon;
export type LayeredElement = Layered & Element;
export type LayeredShape = Layered & Shape;

export const LayeredElement = {
  bbox: (elem: LayeredElement): Bbox => {
    switch (elem.kind) {
      case "rectangle":
        return Rectangle.bbox(elem);
      case "path":
        // TODO(aryap): This isn't the real bbox. The real bbox must consider the segment widths and the start/end extension of the Path.
        return Bbox.fromPoints(...elem.points);
      case "polygon":
        return Polygon.bbox(elem);
      case "ellipse":
        return Ellipse.bbox(elem);
      case "arc":
      // Fallthrough intended.
      default:
        return Bbox.empty();
    }
  },
};

// # Shape Elements
// All of the geometric elements, excluding text.
export type Shape = schema.Shape;
export const Shape = {
  Rectangle,
  Polygon,
  Arc,
  Ellipse,
  Path,
};
